import { EChartsCoreOption } from 'echarts/core';
import { KeyboardEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EChart } from '@/components';
import { LOG_EPSILON } from './utils/axis';

/** Pixels above/below a handle edge that still count as a handle grab target. */
const HANDLE_HIT_PX = 6;
/** Viewport must be at least this tall (px) before resize handles are shown. */
const MIN_HANDLE_VP_HEIGHT = 10;
/** Fraction of visible range to pan per arrow-key press. */
const PAN_STEP_FRACTION = 0.1;

type DragMode = 'pan' | 'resize-top' | 'resize-bottom' | 'select';
type HoverZone = 'top-handle' | 'bottom-handle' | 'body' | 'outside';

export interface YAxisMinimapProps {
  entries: Array<{ lower: number; upper: number; count: number }>;
  yMin: number;
  yMax: number;
  scaleType: 'value' | 'log';
  visibleYRange: { min: number; max: number } | null;
  minimapLeft: number;
  gridTop: number;
  gridHeight: number;
  minimapWidth: number;
  onPan: (min: number, max: number) => void;
  onResetZoom?: () => void;
  theme?: string | Record<string, unknown>;
}

export const YAxisMinimap = memo(function YAxisMinimap({
  entries,
  yMin,
  yMax,
  scaleType,
  visibleYRange,
  minimapLeft,
  gridTop,
  gridHeight,
  minimapWidth: MW,
  onPan,
  onResetZoom,
  theme,
}: YAxisMinimapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    mode: DragMode;
    startY: number;
    startVpTop: number;
    vpHeight: number;
    selectAnchorY: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverZone, setHoverZone] = useState<HoverZone | null>(null);
  const [selectRange, setSelectRange] = useState<{ top: number; bottom: number } | null>(null);

  const yToValue = useCallback(
    (py: number): number => {
      const fraction = 1 - py / gridHeight;
      if (scaleType === 'log') {
        const safeMin = Math.max(LOG_EPSILON, yMin);
        const safeMax = Math.max(LOG_EPSILON, yMax);
        return Math.pow(10, Math.log10(safeMin) + fraction * (Math.log10(safeMax) - Math.log10(safeMin)));
      }
      return yMin + fraction * (yMax - yMin);
    },
    [yMin, yMax, scaleType, gridHeight]
  );

  const valueToY = useCallback(
    (value: number): number => {
      if (scaleType === 'log') {
        const safeMin = Math.max(LOG_EPSILON, yMin);
        const safeMax = Math.max(LOG_EPSILON, yMax);
        const safeVal = Math.max(LOG_EPSILON, value);
        const fraction = (Math.log10(safeVal) - Math.log10(safeMin)) / (Math.log10(safeMax) - Math.log10(safeMin));
        return gridHeight * (1 - fraction);
      }
      const range = yMax - yMin;
      if (range === 0) return 0;
      return gridHeight * (1 - (value - yMin) / range);
    },
    [yMin, yMax, scaleType, gridHeight]
  );

  const vpBounds = useMemo(() => {
    const range = visibleYRange ?? { min: yMin, max: yMax };
    const vpTop = Math.max(0, valueToY(range.max));
    const vpBot = Math.min(gridHeight, valueToY(range.min));
    return { top: vpTop, bottom: vpBot, height: vpBot - vpTop };
  }, [visibleYRange, yMin, yMax, valueToY, gridHeight]);

  const getHoverZone = useCallback(
    (mouseY: number): HoverZone => {
      if (vpBounds.height < MIN_HANDLE_VP_HEIGHT) return 'outside';
      if (Math.abs(mouseY - vpBounds.top) <= HANDLE_HIT_PX) return 'top-handle';
      if (Math.abs(mouseY - vpBounds.bottom) <= HANDLE_HIT_PX) return 'bottom-handle';
      if (vpBounds.height < gridHeight - 1 && mouseY >= vpBounds.top && mouseY <= vpBounds.bottom) return 'body';
      return 'outside';
    },
    [vpBounds, gridHeight]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseY = e.clientY - rect.top;
      const zone = getHoverZone(mouseY);

      if (zone === 'top-handle') {
        dragRef.current = {
          mode: 'resize-top',
          startY: mouseY,
          startVpTop: vpBounds.top,
          vpHeight: vpBounds.height,
          selectAnchorY: 0,
        };
        setIsDragging(true);
      } else if (zone === 'bottom-handle') {
        dragRef.current = {
          mode: 'resize-bottom',
          startY: mouseY,
          startVpTop: vpBounds.top,
          vpHeight: vpBounds.height,
          selectAnchorY: 0,
        };
        setIsDragging(true);
      } else if (zone === 'body') {
        dragRef.current = {
          mode: 'pan',
          startY: mouseY,
          startVpTop: vpBounds.top,
          vpHeight: vpBounds.height,
          selectAnchorY: 0,
        };
        setIsDragging(true);
      } else {
        dragRef.current = { mode: 'select', startY: mouseY, startVpTop: 0, vpHeight: 0, selectAnchorY: mouseY };
        setSelectRange({ top: mouseY, bottom: mouseY });
        setIsDragging(true);
      }
    },
    [vpBounds, getHoverZone]
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.stopPropagation();
      if (!dragRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const mouseY = Math.max(0, Math.min(gridHeight, e.clientY - rect.top));
      const d = dragRef.current;

      if (d.mode === 'pan') {
        const delta = mouseY - d.startY;
        const newTop = Math.max(0, Math.min(gridHeight - d.vpHeight, d.startVpTop + delta));
        onPan(yToValue(newTop + d.vpHeight), yToValue(newTop));
      } else if (d.mode === 'resize-top') {
        const newTop = Math.max(0, Math.min(d.startVpTop + d.vpHeight - 1, mouseY));
        onPan(yToValue(d.startVpTop + d.vpHeight), yToValue(newTop));
      } else if (d.mode === 'resize-bottom') {
        const newBot = Math.min(gridHeight, Math.max(d.startVpTop + 1, mouseY));
        onPan(yToValue(newBot), yToValue(d.startVpTop));
      } else if (d.mode === 'select') {
        const top = Math.min(d.selectAnchorY, mouseY);
        const bottom = Math.max(d.selectAnchorY, mouseY);
        setSelectRange({ top, bottom });
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (dragRef.current?.mode === 'select' && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const mouseY = Math.max(0, Math.min(gridHeight, e.clientY - rect.top));
        const anchorY = dragRef.current.selectAnchorY;
        const top = Math.min(anchorY, mouseY);
        const bottom = Math.max(anchorY, mouseY);
        if (bottom - top > 2) {
          onPan(yToValue(bottom), yToValue(top));
        }
        setSelectRange(null);
      }
      dragRef.current = null;
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, gridHeight, yToValue, onPan]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseY = e.clientY - rect.top;
      setHoverZone(getHoverZone(mouseY));
    },
    [isDragging, getHoverZone]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const range = visibleYRange ?? { min: yMin, max: yMax };
      const span = range.max - range.min;
      const step = span * PAN_STEP_FRACTION;

      switch (e.key) {
        case 'ArrowUp': {
          e.preventDefault();
          const newMax = Math.min(yMax, range.max + step);
          const newMin = newMax - span;
          onPan(Math.max(yMin, newMin), newMax);
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const newMin = Math.max(yMin, range.min - step);
          const newMax = newMin + span;
          onPan(newMin, Math.min(yMax, newMax));
          break;
        }
        case 'Home': {
          e.preventDefault();
          onResetZoom?.();
          break;
        }
        default:
          break;
      }
    },
    [visibleYRange, yMin, yMax, onPan, onResetZoom]
  );

  const { outlineData, maxCount } = useMemo(() => {
    if (entries.length === 0) return { outlineData: [] as Array<[number, number]>, maxCount: 0 };

    let max = 0;
    for (const entry of entries) {
      max = Math.max(max, entry.count);
    }

    const data: Array<[number, number]> = [];
    const logMin = scaleType === 'log' ? Math.max(LOG_EPSILON, yMin) : yMin;
    data.push([0, logMin]);
    data.push([0, entries[0]!.lower]);
    for (const entry of entries) {
      data.push([entry.count, entry.lower]);
      data.push([entry.count, entry.upper]);
    }
    data.push([0, entries[entries.length - 1]!.upper]);
    data.push([0, yMax]);

    return { outlineData: data, maxCount: max };
  }, [entries, yMin, yMax, scaleType]);

  const chartOption = useMemo<EChartsCoreOption>(() => {
    const logMin = scaleType === 'log' ? Math.max(LOG_EPSILON, yMin) : yMin;
    return {
      animation: false,
      grid: { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },
      xAxis: { type: 'value', min: 0, max: maxCount || 1, show: false },
      yAxis: { type: scaleType, min: logMin, max: yMax, show: false },
      series: [
        {
          type: 'line',
          data: outlineData,
          areaStyle: { color: 'rgba(128, 128, 128, 0.3)' },
          lineStyle: { color: 'rgba(128, 128, 128, 0.5)', width: 1 },
          symbol: 'none',
          smooth: false,
          silent: true,
        },
      ],
      tooltip: { show: false },
    };
  }, [outlineData, yMin, yMax, scaleType, maxCount]);

  const left = minimapLeft;

  const activeMode = isDragging ? dragRef.current?.mode : null;
  let cursor = 'crosshair';
  if (activeMode === 'pan') cursor = 'grabbing';
  else if (activeMode === 'resize-top') cursor = 'n-resize';
  else if (activeMode === 'resize-bottom') cursor = 's-resize';
  else if (activeMode === 'select') cursor = 'crosshair';
  else if (hoverZone === 'top-handle') cursor = 'n-resize';
  else if (hoverZone === 'bottom-handle') cursor = 's-resize';
  else if (hoverZone === 'body') cursor = 'grab';

  const showViewport = vpBounds.height < gridHeight - 1;
  const showHandles = vpBounds.height >= MIN_HANDLE_VP_HEIGHT;

  return (
    <div
      ref={containerRef}
      role="slider"
      aria-label="Y-axis viewport"
      aria-valuemin={yMin}
      aria-valuemax={yMax}
      aria-valuenow={visibleYRange ? Math.round((visibleYRange.min + visibleYRange.max) / 2) : yMin}
      tabIndex={0}
      style={{
        position: 'absolute',
        left,
        top: gridTop,
        width: MW,
        height: gridHeight,
        zIndex: 2,
        cursor,
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoverZone(null)}
      onDoubleClick={onResetZoom}
      onKeyDown={handleKeyDown}
    >
      {/* ECharts distribution bars — non-interactive */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <EChart width={MW} height={gridHeight} option={chartOption} theme={theme} />
      </div>

      {/* Select-mode range indicator */}
      {selectRange && (
        <div
          style={{
            position: 'absolute',
            top: selectRange.top,
            left: 0,
            right: 0,
            height: selectRange.bottom - selectRange.top,
            backgroundColor: 'rgba(100, 150, 255, 0.25)',
            border: '1px solid rgba(100, 150, 255, 0.8)',
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Viewport indicator overlay */}
      {showViewport && (
        <>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: vpBounds.top,
              backgroundColor: 'rgba(0, 0, 0, 0.25)',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: gridHeight - vpBounds.bottom,
              backgroundColor: 'rgba(0, 0, 0, 0.25)',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: vpBounds.top,
              left: 0,
              right: 0,
              height: vpBounds.height,
              border: `1px solid ${activeMode ? 'rgba(100, 150, 255, 1)' : 'rgba(100, 150, 255, 0.7)'}`,
              boxSizing: 'border-box',
              pointerEvents: 'none',
            }}
          />
        </>
      )}
      {/* Resize handles */}
      {showHandles && (
        <>
          <div
            style={{
              position: 'absolute',
              top: Math.max(0, vpBounds.top - 2),
              left: 4,
              right: 4,
              height: 4,
              backgroundColor: 'rgba(100, 150, 255, 0.8)',
              borderRadius: 2,
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: Math.min(gridHeight - 4, vpBounds.bottom - 2),
              left: 4,
              right: 4,
              height: 4,
              backgroundColor: 'rgba(100, 150, 255, 0.8)',
              borderRadius: 2,
              pointerEvents: 'none',
            }}
          />
        </>
      )}
    </div>
  );
});
